// Small building blocks used by the editor, the view and the action panels. Built
// locally per the kit's rule (src/components/ui.tsx); candidates for the shared kit.
import type { ReactNode } from 'react'
import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { blockerText, RuleRefs } from '@/components/evidence'
import { Badge, Button, Checkbox, cx, TextInput } from '@/components/ui'
import type { Blocker } from '@/domain/types'
import type { PolicyParam, ToneName } from './model'
import { VALUE_STATUS_TEXT } from './model'

/** A question with its own legend: use for radio groups and checkbox sets. */
export function Question({ legend, hint, required, errors, children }: {
  legend: ReactNode
  hint?: ReactNode
  required?: boolean
  errors?: Blocker[]
  children: ReactNode
}) {
  return (
    <fieldset className="mb-4 min-w-0 border-0 p-0">
      <legend className="mb-1 text-sm font-semibold text-ink">
        {legend}
        {required ? (
          <span className="ml-1 text-bad" aria-hidden>
            *
          </span>
        ) : null}
      </legend>
      {hint ? <p className="mb-2 text-xs text-ink-soft">{hint}</p> : null}
      {children}
      <FieldErrors errors={errors} />
    </fieldset>
  )
}

/** Server blocker messages for one input, from the last submit attempt. */
export function FieldErrors({ errors }: { errors?: Blocker[] }) {
  if (!errors || errors.length === 0) return null
  return (
    <ul className="mt-1 space-y-0.5">
      {errors.map((b, i) => (
        <li key={`${b.code}-${i}`} className="text-xs font-semibold text-bad">
          <span aria-hidden>! </span>
          {blockerText(b)}
          <RuleRefs ruleIds={b.rule_ids} decisionIds={b.decision_ids} />
        </li>
      ))}
    </ul>
  )
}

/** Field error text for the kit's <Field error> slot. */
export function fieldError(errors: Blocker[]): ReactNode {
  return errors.length ? errors.map((b) => b.message).join(' ') : undefined
}

/** Explicit Yes / No. Nothing is selected until the person answers. */
export function YesNo({ name, value, onChange, disabled, yesLabel = 'Yes', noLabel = 'No' }: {
  name: string
  value: boolean | null
  onChange: (v: boolean) => void
  disabled?: boolean
  yesLabel?: string
  noLabel?: string
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {([true, false] as const).map((v) => (
        <label
          key={String(v)}
          className={cx(
            'flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm',
            value === v ? 'border-brand bg-brand-light font-semibold text-ink' : 'border-line bg-white text-ink',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <input type="radio" name={name} checked={value === v} disabled={disabled} onChange={() => onChange(v)} />
          {v ? yesLabel : noLabel}
        </label>
      ))}
    </div>
  )
}

/** The statement a person signs, with a deliberate tick before the sign button works. */
export function Declaration({ text, checked, onChange, disabled }: {
  text: ReactNode
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className="rounded-lg border-2 border-brand/40 bg-brand-light p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-dark">What you are signing</p>
      <p className="mt-1 text-sm font-semibold text-ink">{text}</p>
      <Checkbox
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        label="I make this declaration"
        hint="The server records your name, role, qualification and the server time with your signature."
      />
    </div>
  )
}

/** Whole-number input that never silently changes what was typed. */
export function IntegerInput({ id, value, onChange, min = 0, placeholder, disabled }: {
  id?: string
  value: number | null
  onChange: (v: number | null) => void
  min?: number
  placeholder?: string
  disabled?: boolean
}) {
  const parse = (t: string): number | null | 'invalid' => {
    if (t.trim() === '') return null
    const n = Number(t)
    return /^\s*\d+\s*$/.test(t) && Number.isInteger(n) && n >= min ? n : 'invalid'
  }
  const [text, setText] = useState(value === null ? '' : String(value))
  useEffect(() => {
    // Follow outside changes (restore, reload) but keep what is being typed.
    setText((t) => {
      const p = parse(t)
      if (p === value || (p === 'invalid' && value === null)) return t
      return value === null ? '' : String(value)
    })
    // parse depends only on min, which does not change for one input
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  const invalid = parse(text) === 'invalid'
  return (
    <>
      <TextInput
        id={id}
        inputMode="numeric"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={(e) => {
          const t = e.target.value
          setText(t)
          const p = parse(t)
          onChange(p === 'invalid' ? null : p)
        }}
      />
      {invalid ? <p className="mt-1 text-xs font-semibold text-bad">Enter a whole number{min > 0 ? ` of ${min} or more` : ''}. This value is not kept until it is valid.</p> : null}
    </>
  )
}

/** Editable list of short texts (evidence references, participants are separate). */
export function TextListEditor({ values, onChange, addLabel, placeholder, label }: {
  values: string[]
  onChange: (next: string[]) => void
  addLabel: string
  placeholder?: string
  label: string
}) {
  const baseId = useId()
  return (
    <div className="space-y-2">
      {values.map((v, i) => (
        <div key={i} className="flex gap-2">
          <label htmlFor={`${baseId}-${i}`} className="sr-only">
            {label} {i + 1}
          </label>
          <TextInput id={`${baseId}-${i}`} value={v} placeholder={placeholder} onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))} />
          <Button variant="secondary" type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}>
            Remove
          </Button>
        </div>
      ))}
      <Button variant="secondary" type="button" onClick={() => onChange([...values, ''])}>
        {addLabel}
      </Button>
    </div>
  )
}

/** Two-tap removal so a slip of the thumb never deletes typed content. */
export function RemoveButton({ label, onConfirm }: { label: string; onConfirm: () => void }) {
  const [armed, setArmed] = useState(false)
  if (!armed) {
    return (
      <Button variant="ghost" type="button" onClick={() => setArmed(true)}>
        {label}
      </Button>
    )
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="text-sm text-ink-soft">Remove it?</span>
      <Button
        variant="danger"
        type="button"
        onClick={() => {
          // Disarm first: lists are keyed by position, so this button moves to the next row.
          setArmed(false)
          onConfirm()
        }}
      >
        Yes, remove
      </Button>
      <Button variant="secondary" type="button" onClick={() => setArmed(false)}>
        Keep
      </Button>
    </span>
  )
}

/** A policy value with its status and sources, never presented as approved policy. */
export function PolicyNote({ param, children }: { param: PolicyParam | null; children: ReactNode }) {
  if (!param) return null
  return (
    <p className="mt-1 text-xs text-ink-soft">
      {children} <span className="font-semibold">({VALUE_STATUS_TEXT[param.value_status] ?? param.value_status.replace(/_/g, ' ')})</span>
      <RuleRefs ruleIds={param.rule_ids} decisionIds={param.decision_ids} />
    </p>
  )
}

export function StatusBadge({ status }: { status: { label: string; tone: ToneName } | undefined }) {
  if (!status) return null
  return <Badge tone={status.tone}>{status.label}</Badge>
}

/** Multi-line free text as entered; blank shows "Not given". */
export function TextBlock({ text, empty = 'Not given' }: { text: string | null | undefined; empty?: string }) {
  if (!text || !text.trim()) return <span className="text-ink-faint">{empty}</span>
  return <span className="whitespace-pre-wrap break-words">{text}</span>
}

/** A router link that looks like a button (never a <button> inside an <a>). */
export function LinkButton({ to, children, variant = 'primary' }: { to: string; children: ReactNode; variant?: 'primary' | 'secondary' }) {
  return (
    <Link
      to={to}
      className={cx(
        'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg px-4 font-semibold transition',
        variant === 'primary' ? 'bg-brand text-white hover:bg-brand-dark' : 'border border-line-strong bg-card text-brand-dark hover:bg-brand-light',
      )}
    >
      {children}
    </Link>
  )
}

export function SubHeading({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-2 mt-4 flex flex-wrap items-center justify-between gap-2 first:mt-0">
      <h3 className="text-sm font-bold uppercase tracking-wide text-ink-soft">{children}</h3>
      {aside}
    </div>
  )
}
